/**
 * runComparison.ts
 *
 * Main-thread client for the Compare Slides feature. Orchestrates the pipeline:
 *
 *   1. Extract text from both decks (stays here — DOMParser is main-thread only).
 *   2. Hand the structured slide models to a Web Worker that runs the heavy
 *      O(N^2) diff, so the task pane never freezes on large decks.
 *
 * Falls back to running the diff inline when Web Workers are unavailable in the
 * Office host webview (or worker construction throws).
 */
import { extractAllSlidesText, type ExtractedShape, type ExtractedSlide } from "./pptxTextExtractor";
import { generateComparisonReport, type ComparisonReport } from "./textComparer";

// Public type aliases for this feature's data models, re-exported from the
// modules that own them so every consumer (worker, panel, hook) shares one
// definition instead of a structurally-looser duplicate.

/** A single shape's extracted text, as produced by pptxTextExtractor. */
export type SlideShape = ExtractedShape;

/** One slide's extracted model. Structured-clone-safe for postMessage. */
export type SlideModel = ExtractedSlide;

/** The comparison report returned by generateComparisonReport. */
export type { ComparisonReport };

/** Message posted to the worker. */
export interface CompareRequest {
  originalSlides: SlideModel[];
  modifiedSlides: SlideModel[];
}

/** Message posted back from the worker. */
export type CompareResponse =
  | { ok: true; report: ComparisonReport }
  | { ok: false; error: string };

// Hard ceiling on the worker run. If the worker wedges (or its callback never
// fires), we fail fast instead of leaving the Compare button spinning forever.
const WORKER_TIMEOUT_MS = 30000;

/**
 * Compare an original deck against the current one and return the report.
 * Extraction runs on the main thread; the diff runs in a worker.
 *
 * @param origBuffer    raw bytes of the uploaded original .pptx
 * @param currentBytes  raw bytes of the live deck
 * @param opts.signal   abort the comparison (e.g. on panel unmount)
 */
export async function compareDecks(
  origBuffer: ArrayBuffer,
  currentBytes: ArrayBuffer,
  opts: { signal?: AbortSignal } = {},
): Promise<ComparisonReport> {
  const { signal } = opts;
  if (signal?.aborted) throw new DOMException("Aborted", "AbortError");

  // Extraction must stay on the main thread — DOMParser is unavailable in workers.
  const [originalSlides, modifiedSlides] = await Promise.all([
    extractAllSlidesText(origBuffer),
    extractAllSlidesText(currentBytes),
  ]);

  if (signal?.aborted) throw new DOMException("Aborted", "AbortError");

  // No worker support (some host webviews) → run the diff inline.
  if (typeof Worker === "undefined") {
    return generateComparisonReport(originalSlides, modifiedSlides);
  }

  return runInWorker({ originalSlides, modifiedSlides }, signal);
}

/** Run the diff in a dedicated worker with timeout + abort handling. */
function runInWorker(req: CompareRequest, signal?: AbortSignal): Promise<ComparisonReport> {
  return new Promise<ComparisonReport>((resolve, reject) => {
    let worker: Worker;
    try {
      worker = new Worker(new URL("./compareSlides.worker.ts", import.meta.url), {
        type: "module",
      });
    } catch {
      // Worker construction failed — fall back to the inline diff.
      try {
        resolve(generateComparisonReport(req.originalSlides, req.modifiedSlides));
      } catch (err) {
        reject(err instanceof Error ? err : new Error(String(err)));
      }
      return;
    }

    let settled = false;
    const onAbort = () => finish(() => reject(new DOMException("Aborted", "AbortError")));

    const cleanup = () => {
      clearTimeout(timer);
      signal?.removeEventListener("abort", onAbort);
      worker.terminate();
    };
    const finish = (action: () => void) => {
      if (settled) return;
      settled = true;
      cleanup();
      action();
    };

    const timer = setTimeout(() => {
      finish(() =>
        reject(new Error(`Slide comparison timed out after ${WORKER_TIMEOUT_MS / 1000}s.`)),
      );
    }, WORKER_TIMEOUT_MS);

    if (signal) {
      if (signal.aborted) {
        onAbort();
        return;
      }
      signal.addEventListener("abort", onAbort);
    }

    worker.onmessage = (e: MessageEvent<CompareResponse>) => {
      const data = e.data;
      if (data.ok) finish(() => resolve(data.report));
      else finish(() => reject(new Error(data.error)));
    };

    worker.onerror = (e) => {
      finish(() => reject(new Error(e.message || "Slide comparison worker failed.")));
    };

    worker.postMessage(req);
  });
}
